/**
 * Conversations API.
 *
 * Endpoints for the saved-view picker (`ConversationProfile` CRUD) and the
 * thread list (`ListThreads`). Profile mutations are REST-shaped; the
 * thread list uses POST + body because `ConversationFilters` is a complex
 * object that does not serialize cleanly to query params.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ConversationFiltersSchema } from "../../../shared/domain/conversation/conversation-filters.schema";
import {
  ThreadCursorSchema,
  ThreadMessageCursorSchema,
  ThreadMessageSchema,
  ThreadStatusSelectionSchema,
  ThreadSummarySchema
} from "../../../shared/domain/conversation/thread.schemas";
import { ThreadSatisfactionSchema } from "../../../shared/domain/conversation/thread-satisfaction.schemas";
import { DOWNLOAD_FILE_FORMATS } from "../../../shared/domain/file/file-format-mime";
import { ApiSuccessSchema } from "../../common/response.schema";

// Re-export so existing consumers (api/index.ts) keep working unchanged.
export {
  ThreadCursorSchema,
  ThreadMessageCursorSchema,
  ThreadMessageSchema,
  ThreadSatisfactionSchema,
  ThreadStatusSelectionSchema,
  ThreadSummarySchema
};

/**
 * Wire-format profile — Date columns serialize to ISO strings before
 * leaving the controller; the response schema validates strings.
 */
export const ConversationProfileSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  userId: z.string().nullable(),
  name: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  filters: ConversationFiltersSchema,
  sortBy: z.nativeEnum(DbEnum.ConversationSort),
  position: z.number().int(),
  isSystem: z.boolean(),
  isPinned: z.boolean(),
  visibility: z.nativeEnum(DbEnum.ConversationProfileVisibility),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Read-model schemas moved to shared/domain/conversation/thread.schemas.ts so
// they can be referenced both here (HTTP responses) and from
// shared/websocket/events.payloads.ts (WS realtime payloads) without
// reversing the package's `api/` → `shared/` dependency direction.
// They are re-exported above so existing consumers keep working.

const PathProfileId = z.object({ profileId: z.string().uuid() });
const PathThreadId = z.object({ threadId: z.string().uuid() });
const PathMessageId = z.object({ messageId: z.string().uuid() });
const PathCustomerId = z.object({ customerId: z.string().uuid() });

/**
 * WhatsApp template payload shipped from the composer to the conversations
 * template-send endpoint. Mirrors the shape the deployments
 * `ListWhatsappTemplates` endpoint hands out — `id` is the Twilio
 * Content SID (e.g. `HXa5da3a44318858279eb1eef6a7d5a023`), `language`
 * is the ISO code, and `types` is the per-content-type bag (`twilio/text`,
 * `twilio/quick-reply`, etc.). `types` is structurally opaque because
 * Twilio's content type catalog evolves independently — the conversations
 * layer treats it as transport-only and the legacy WhatsApp send pipeline
 * does the per-type interpretation.
 */
const WhatsappTemplatePayloadSchema = z.object({
  id: z.string().min(1),
  language: z.string().min(1),
  types: z.record(z.string(), z.unknown())
});

export const ZConversations = {
  /**
   * List the caller's visible profiles in the current org (own +
   * org-shared). Lazy-seeds the four system defaults on first call.
   */
  ListProfiles: {
    method: "GET" as const,
    path: "/api/conversations/profiles",
    Response: ApiSuccessSchema(
      z.object({
        profiles: z.array(ConversationProfileSchema)
      })
    )
  },

  /**
   * Create a new profile owned by the caller (PRIVATE) or shared
   * org-wide (ORG, requires admin — server-side check).
   */
  CreateProfile: {
    method: "POST" as const,
    path: "/api/conversations/profiles",
    Body: z.object({
      name: z.string().trim().min(1).max(120),
      icon: z.string().max(64).nullable().optional(),
      color: z.string().max(32).nullable().optional(),
      filters: ConversationFiltersSchema,
      sortBy: z.nativeEnum(DbEnum.ConversationSort).optional(),
      visibility: z.nativeEnum(DbEnum.ConversationProfileVisibility).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        profile: ConversationProfileSchema
      })
    )
  },

  /**
   * Partial update — only fields explicitly present in the body are
   * applied. Use `icon: null` / `color: null` to clear them; an
   * omitted field is left unchanged.
   */
  UpdateProfile: {
    method: "PATCH" as const,
    path: "/api/conversations/profiles/:profileId",
    PathVars: PathProfileId,
    Body: z.object({
      name: z.string().trim().min(1).max(120).optional(),
      icon: z.string().max(64).nullable().optional(),
      color: z.string().max(32).nullable().optional(),
      filters: ConversationFiltersSchema.optional(),
      sortBy: z.nativeEnum(DbEnum.ConversationSort).optional(),
      position: z.number().int().min(0).optional(),
      visibility: z.nativeEnum(DbEnum.ConversationProfileVisibility).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        profile: ConversationProfileSchema
      })
    )
  },

  /**
   * Delete a profile. System-seeded profiles return 403; foreign
   * profiles return 403 unless the caller is org-admin on a shared
   * profile.
   */
  DeleteProfile: {
    method: "DELETE" as const,
    path: "/api/conversations/profiles/:profileId",
    PathVars: PathProfileId,
    Response: ApiSuccessSchema(z.object({}))
  },

  /**
   * Pin or unpin the profile as the caller's default landing. At
   * most one of the caller's profiles is pinned at a time — the
   * server clears other pins atomically when `pinned=true`.
   */
  PinProfile: {
    method: "POST" as const,
    path: "/api/conversations/profiles/:profileId/pin",
    PathVars: PathProfileId,
    Body: z.object({ pinned: z.boolean() }),
    Response: ApiSuccessSchema(
      z.object({
        profile: ConversationProfileSchema
      })
    )
  },

  /**
   * Paginated thread list. POST + body because `ConversationFilters` is a
   * complex multi-axis object that does not encode cleanly into query
   * params. The use case caps `limit` at 100 server-side; default is 30.
   */
  ListThreads: {
    method: "POST" as const,
    path: "/api/conversations/threads/list",
    Body: z.object({
      filters: ConversationFiltersSchema,
      sortBy: z.nativeEnum(DbEnum.ConversationSort).optional(),
      limit: z.number().int().min(1).max(100).optional(),
      cursor: ThreadCursorSchema.nullable().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        threads: z.array(ThreadSummarySchema),
        nextCursor: ThreadCursorSchema.nullable()
      })
    )
  },

  /**
   * Resolve a UUID-or-nanoId external reference to the canonical
   * thread UUID. Powers the conversations module's deep-link
   * hydration surface — legacy inbox URLs carry a chat-id query param
   * whose shape depends on the originating site (Contacts emits a
   * nanoId via `chatNanoId`, telegram-stats emits a UUID via
   * `chat-id`), and the resolved UUID feeds straight into the slice's
   * `setSelection` action. A miss returns 404 — the frontend falls
   * back to the bare inbox so a stale deep-link does not block the
   * operator.
   *
   * Static path lives BEFORE `:threadId` in the controller so the
   * dynamic UUID matcher never swallows the literal "resolve" segment.
   */
  ResolveThread: {
    method: "GET" as const,
    path: "/api/conversations/threads/resolve",
    Query: z.object({
      // Tight upper bound. The two shapes the adapter handles are a
      // 36-char UUID and an ~11-char nanoId — 64 chars leaves
      // headroom for future ID variants without inviting unbounded
      // input. Wider strings get rejected at the API boundary
      // before hitting Prisma.
      ref: z.string().min(1).max(64)
    }),
    Response: ApiSuccessSchema(
      z.object({
        threadId: z.string().uuid()
      })
    )
  },

  /**
   * Single thread + most recent page of messages, in one round-trip so
   * the detail pane has everything it needs on first paint.
   * `messageLimit` caps at 200 server-side; default is 50. Older pages
   * fetch via a separate cursor-paginated endpoint when the scrollback
   * UX lands.
   */
  GetThread: {
    method: "GET" as const,
    path: "/api/conversations/threads/:threadId",
    PathVars: PathThreadId,
    Query: z.object({
      messageLimit: z.coerce.number().int().min(1).max(200).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema,
        messages: z.array(ThreadMessageSchema),
        nextMessageCursor: ThreadMessageCursorSchema.nullable()
      })
    )
  },

  /**
   * Mark a thread as read for the calling user.
   *
   * Idempotent: repeated calls overwrite `lastReadAt = now()`. Nullary
   * body — the only inputs are the path var and the caller identity
   * (server-side from JWT). Response carries the resolved `lastReadAt`
   * so the frontend can patch the same cache shape it gets from the
   * realtime broadcast (`conversations.read-state.updated`).
   */
  MarkThreadRead: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/read",
    PathVars: PathThreadId,
    Response: ApiSuccessSchema(
      z.object({
        lastReadAt: z.string().datetime()
      })
    )
  },

  /**
   * Mark a thread as unread for the calling user.
   *
   * Idempotent: repeated calls overwrite `lastReadAt = null`. Nullary
   * body. Mirror of `MarkThreadRead` with `lastReadAt: null` in the
   * response — frontend uses the same patch path either way.
   */
  MarkThreadUnread: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/unread",
    PathVars: PathThreadId,
    Response: ApiSuccessSchema(
      z.object({
        lastReadAt: z.null()
      })
    )
  },

  /**
   * Send an agent reply into a thread.
   *
   * `content` accepts plain text up to 50000 chars (matches the embed
   * widget cap — the largest precedent for user-typed chat content; no
   * separate `MAX_MESSAGE_LENGTH` constant exists in the legacy code).
   * `messageId` is optional; when present (frontend-generated UUID for
   * the optimistic insert) the server respects it as the row's primary
   * key so the realtime broadcast collapses with the optimistic cache
   * row on the same id.
   *
   * Channel dispatch (the message reaching the customer through Slack /
   * WhatsApp / etc.) is intentionally OUT of scope for this endpoint —
   * chapter 07 lands the per-channel send. After this endpoint ships,
   * agents see their replies appear in the thread in real time across
   * tabs, but the customer does not yet receive them on the original
   * channel.
   */
  SendMessage: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/messages",
    PathVars: PathThreadId,
    Body: z.object({
      content: z.string().trim().min(1).max(50000),
      messageId: z.string().uuid().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        message: ThreadMessageSchema
      })
    )
  },

  /**
   * Update a thread's status.
   *
   * Body carries the synthesized `ThreadStatusSelection` enum (5 values)
   * which the server expands to the legacy two-column shape on `Chat`:
   * "CLOSED" → `(status = ARCHIVED, ticketStatus unchanged)`, the other
   * four → `(status = OPEN, ticketStatus = <selection>)`. The new
   * contract hides the two-column legacy combo behind a single user-
   * facing enum; the wire still surfaces both columns on `ThreadSummary`
   * so the frontend can derive the selection at render time.
   *
   * Response carries the freshly-read `ThreadSummary` so optimistic
   * cache patches can land the canonical server-resolved shape (status
   * + ticketStatus + updatedAt + closedAt all bumped together).
   */
  UpdateThreadStatus: {
    method: "PATCH" as const,
    path: "/api/conversations/threads/:threadId/status",
    PathVars: PathThreadId,
    Body: z.object({
      status: ThreadStatusSelectionSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema
      })
    )
  },

  /**
   * Append a note to a thread.
   *
   * Notes are stored on `Chat.comments` (legacy column name preserved
   * for parallel-run; the wire field is `notes` per the user-facing
   * label). Each note's disk shape is `{content, date, userId?}`
   * (legacy preserved during parallel-run); the read mapper renames
   * `date → createdAt` and normalizes the optional `userId → null`
   * before the wire.
   *
   * Body carries only the note content; `userId` (caller) and `date`
   * (server now()) are filled server-side from the JWT context and
   * the clock. Append-only — no edit / delete affordance in legacy,
   * preserved here.
   *
   * Response wraps the freshly-read ThreadSummary so the optimistic
   * cache patch can land the canonical server-resolved notes array
   * (including the just-appended note's exact server-resolved ISO
   * timestamp) without merging partials.
   */
  AppendThreadNote: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/notes",
    PathVars: PathThreadId,
    Body: z.object({
      content: z.string().trim().min(1).max(10000)
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema
      })
    )
  },

  /**
   * Replace the full set of users assigned to a thread.
   *
   * Replace-all semantic — body carries the new full set, server
   * resets `Chat.assignedUsers` to that set in one transaction.
   * "Assign to me" / "Unassign me" toggles by adding/removing the
   * caller's userId from the array client-side. Empty array = clear
   * all assignees.
   *
   * userIds are Clerk user identifiers. The picker on the frontend
   * sources them from the active org's memberships, and the SERVER
   * proves that independently — a client-sourced list is a claim
   * about the caller's UI, not about the organization.
   *
   * 🚨 `max(50)` IS A SHAPE CONSTRAINT AND PROVES NOTHING ABOUT
   * OWNERSHIP. Neither does the M:N relation's foreign key: it keys
   * into the GLOBAL `User` table, so an id naming no user at all does
   * roll the transaction back, while an id naming a real user in
   * ANOTHER ORGANIZATION resolves cleanly and would be written. A
   * `User` row carries no organization — membership is a separate
   * table — so the key proves the person exists and never whose they
   * are.
   *
   * The proof is `SetThreadAssigneesUseCase`, which reads
   * `OrganizationMembership` via `LIST_ORGANIZATION_USER_IDS_PORT`
   * and answers 400 before the write. It refuses BEFORE rather than
   * after because `INBOX_ASSIGNED_TO_ME` is dispatched per
   * newly-added user off the post-write set, so a late guard would
   * leave a foreign member already notified about a thread they
   * cannot see.
   */
  SetThreadAssignees: {
    method: "PATCH" as const,
    path: "/api/conversations/threads/:threadId/assignees",
    PathVars: PathThreadId,
    Body: z.object({
      userIds: z.array(z.string()).max(50)
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema
      })
    )
  },

  /**
   * Update a thread's operator-editable title (`Chat.topic`).
   *
   * Body carries the new topic string (1..255 chars) or null to
   * clear. The frontend inline-edit treats an empty trim as "clear",
   * so the wire accepts null explicitly. The legacy AI auto-namer
   * lives in the chats/ pipeline and continues to backfill topics on
   * AI-processed chats; this endpoint is the operator override.
   */
  UpdateThreadTopic: {
    method: "PATCH" as const,
    path: "/api/conversations/threads/:threadId/topic",
    PathVars: PathThreadId,
    Body: z.object({
      topic: z.string().trim().max(255).nullable()
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema
      })
    )
  },

  /**
   * Update a thread's response-handling mode.
   *
   * `responseHandling` controls whether the AI agent answers the
   * thread directly (`AUTO`), waits for operator approval before
   * sending (`ON_APPROVAL`), or is silent on this thread (`MANUAL`).
   * Single-column write on `Chat.responseHandling`; mirror of
   * UpdateThreadStatus on the contract shape.
   *
   * The frontend pane hides this control for GMAIL + EMBED deployments
   * (legacy parity — different control surfaces own the response model
   * for those channels). The server-side endpoint accepts any value
   * regardless of channel; the hide rule is a UI-only constraint and
   * channel-specific runtime side-effects (chapter 8 scope) are not
   * gated here.
   */
  UpdateThreadResponseHandling: {
    method: "PATCH" as const,
    path: "/api/conversations/threads/:threadId/response-handling",
    PathVars: PathThreadId,
    Body: z.object({
      responseHandling: z.nativeEnum(DbEnum.ChatResponseHandling)
    }),
    Response: ApiSuccessSchema(
      z.object({
        thread: ThreadSummarySchema
      })
    )
  },

  /**
   * Validate a `WAITING_VALIDATION` AI draft and dispatch it on the
   * channel. Body carries the operator's possibly-edited content —
   * the validate flow IS the edit flow (legacy parity: there is no
   * separate edit endpoint, the textarea ships its current value
   * through here). Server flips `Message.status → PROCESSED`,
   * persists the new content, self-attaches the validating user
   * (additive M:N add → fires `INBOX_ASSIGNED_TO_ME`), dispatches via
   * the channel pipeline (best-effort — failure surfaces via
   * `Message.deliveryStatus = FAILED`), and refreshes the chat's
   * pending-message count + lastMessage.
   *
   * Response wraps the freshly-read `ThreadMessage` (now PROCESSED)
   * so the optimistic cache patch lands the canonical server-resolved
   * shape on success.
   */
  ValidatePendingMessage: {
    method: "POST" as const,
    path: "/api/conversations/messages/:messageId/validate",
    PathVars: PathMessageId,
    Body: z.object({
      content: z.string().trim().min(1).max(10000)
    }),
    Response: ApiSuccessSchema(
      z.object({
        message: ThreadMessageSchema
      })
    )
  },

  /**
   * Soft-delete a `WAITING_VALIDATION` AI draft. Status flips to
   * `INVALIDATED` and `Chat.numberOfPendingMessages` decrements; the
   * row stays on disk (legacy parity — useful for audit /
   * observability and harmless in the UI because all PROCESSED-
   * filtered queries skip it).
   *
   * Response wraps the freshly-read `ThreadMessage` (now INVALIDATED)
   * so the optimistic cache patch lands the canonical shape; the
   * conversations cache keeps the row as a soft-deleted record so
   * counts + ordering stay stable across the patch.
   */
  InvalidatePendingMessage: {
    method: "DELETE" as const,
    path: "/api/conversations/messages/:messageId",
    PathVars: PathMessageId,
    Response: ApiSuccessSchema(
      z.object({
        message: ThreadMessageSchema
      })
    )
  },

  /**
   * Render a message's text content as a downloadable file (PDF, DOCX,
   * or plain text).
   *
   * Secured org-scoped sibling of the legacy public `chatrooms/messages/
   * generate-file` endpoint — the conversations download surface
   * enforces tenancy server-side via `FindMessageByIdPort` (a foreign-
   * org `messageId` resolves to a 404, never the message content).
   *
   * Response is the rendered binary stream — `responseType: "blob"`
   * signals codegen to skip Zod response validation and surface a
   * `Blob` to the frontend. The MIME type + `Content-Disposition`
   * header are set by the controller per the requested `fileFormat`.
   *
   * `fileFormat` is the narrow three-value set the inbox UI exposes;
   * the legacy `AllowedFileFormats` enum carries image / audio /
   * archive variants the conversations download does not support.
   */
  DownloadMessageFile: {
    method: "POST" as const,
    path: "/api/conversations/messages/:messageId/file",
    PathVars: PathMessageId,
    Body: z.object({
      fileFormat: z.enum(DOWNLOAD_FILE_FORMATS)
    }),
    responseType: "blob" as const
  },

  /**
   * Persist an operator-submitted satisfaction rating (👍 / 👎) for an
   * outbound bubble.
   *
   * Body carries `score: -1 | 1` (thumbs only on the conversations
   * surface — the underlying SatisfactionScore table accepts -1..10
   * to support a richer public-API rating scale, but the inbox UI
   * is set-and-done thumbs). `comment` is optional, capped at 1000
   * chars (legacy parity); the same endpoint handles the second call
   * after the operator types into the comment popover (upsert
   * idempotent on score by construction).
   *
   * Response wraps the freshly-read `ThreadMessage` so the optimistic
   * cache patch lands the canonical server-resolved shape.
   */
  SaveMessageSatisfaction: {
    method: "POST" as const,
    path: "/api/conversations/messages/:messageId/satisfaction",
    PathVars: PathMessageId,
    Body: z.object({
      score: z.union([z.literal(-1), z.literal(1)]),
      comment: z.string().trim().max(1000).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        message: ThreadMessageSchema
      })
    )
  },

  /**
   * Soft-delete a thread by flipping `Chat.status` to DELETED. The
   * row stays on disk so message history and analytics are intact;
   * inbox queries (which all filter `status NOT DELETED`) drop it
   * from the operator's view. The probe + write both use a
   * `(id, organizationId)` tenancy guard with no status filter, so
   * a second call against the same already-DELETED id is a 200 no-op
   * (the soft-flip is idempotent). 404 surfaces cross-tenant id
   * reuse.
   */
  DeleteThread: {
    method: "DELETE" as const,
    path: "/api/conversations/threads/:threadId",
    PathVars: PathThreadId,
    Response: ApiSuccessSchema(z.object({}))
  },

  /**
   * Last-N threads for one customer, surfaced in the right pane's
   * "past conversations" panel.
   *
   * GET shape because the read is side-effect free and the inputs are
   * simple scalars. `excludeThreadId` lets the caller suppress the
   * thread already showing in the detail pane so the panel never
   * echoes the active row. `limit` is capped at 20 server-side; the
   * panel typically asks for 5.
   */
  ListThreadsForCustomer: {
    method: "GET" as const,
    path: "/api/conversations/customers/:customerId/threads",
    PathVars: PathCustomerId,
    Query: z.object({
      excludeThreadId: z.string().uuid().optional(),
      limit: z.coerce.number().int().min(1).max(20).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        threads: z.array(ThreadSummarySchema)
      })
    )
  },

  /**
   * Framework-aware satisfaction scores for one thread, surfaced in the
   * right-pane satisfaction section.
   *
   * Returns the latest score first. The caller (frontend) decides how
   * to fold LEGACY rows out of the visible list once any
   * framework-aware row exists — the wire returns them all so the
   * client can still render a legacy-only thread without a second
   * round-trip.
   */
  ListThreadSatisfaction: {
    method: "GET" as const,
    path: "/api/conversations/threads/:threadId/satisfaction",
    PathVars: PathThreadId,
    Response: ApiSuccessSchema(
      z.object({
        scores: z.array(ThreadSatisfactionSchema)
      })
    )
  },

  /**
   * Send an approved WhatsApp template into a thread.
   *
   * Operator-facing surface for the "outside the 24h reply window"
   * re-engagement path. Body carries the template descriptor (Twilio
   * Content SID + language + types bag) and any variable substitutions;
   * the underlying WhatsApp send pipeline lives in the chats module
   * (Twilio API call + status polling + Message row creation) — the
   * conversations endpoint wraps it to enforce thread tenancy at the
   * conversations boundary and to fire `conversations.message.created`
   * + `conversations.thread.updated` on the in-process bus so the open
   * inbox UI picks the new outbound row up through the same socket
   * cache flow the SendMessage path uses.
   *
   * `deliveryStatus` mirrors Twilio's terminal status string
   * (`sent`, `delivered`, `failed`, etc.) — null when the upstream
   * fetch errored before the row landed. The operator-side delivery
   * badge in the bubble reads from `Message.statusInfos.templateMessageStatus`
   * via the same pipe; this field is for the immediate response's
   * optimistic UI patch.
   */
  SendThreadTemplate: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/template",
    PathVars: PathThreadId,
    Body: z.object({
      template: WhatsappTemplatePayloadSchema,
      templateData: z.record(z.string(), z.string())
    }),
    Response: ApiSuccessSchema(
      z.object({
        message: ThreadMessageSchema,
        deliveryStatus: z.string().nullable()
      })
    )
  },

  /**
   * Regenerate the AI agent's last response on a thread.
   *
   * Nullary body — the server walks the chat history to the most recent
   * HUMAN message and re-invokes the assistant with it. The underlying
   * regenerate pipeline lives in the chats module (Run row, AI invoke,
   * tool calls, persistence); the conversations endpoint wraps it to
   * enforce thread tenancy and fire `conversations.message.created` for
   * every newly persisted message + `conversations.thread.updated` once.
   *
   * Body uses `passthrough()` so older clients sending no body, and
   * future clients sending an optional flag we haven't designed yet,
   * both validate without breaking the contract on rollback.
   *
   * Response is a discriminated union — `status: "ok"` carries the
   * newly persisted `ThreadMessage[]` (may be empty on legitimate
   * no-op invokes); `status: "ai_failed"` signals the legacy AI invoke
   * errored mid-flight (200 HTTP because the previous AI bubble was
   * marked FAILED in the DB during the call — the request itself
   * succeeded at the persistence layer; the failure surface lives on
   * the bubble status, not on the HTTP envelope). Frontends MUST
   * branch on the discriminant before showing a success toast.
   */
  RegenerateThreadMessage: {
    method: "POST" as const,
    path: "/api/conversations/threads/:threadId/regenerate",
    PathVars: PathThreadId,
    Body: z.object({}).passthrough(),
    Response: ApiSuccessSchema(
      z.discriminatedUnion("status", [
        z.object({
          status: z.literal("ok"),
          messages: z.array(ThreadMessageSchema)
        }),
        z.object({
          status: z.literal("ai_failed")
        })
      ])
    )
  },

  /**
   * Older-page pagination for the detail-pane scrollback.
   *
   * `GetThread` returns the first (newest) page; this endpoint walks
   * backwards from a cursor (the previous response's `nextCursor`).
   * Cursor is split into two flat query params (`cursorCreatedAt` +
   * `cursorMessageId`) rather than JSON-encoded — flat scalars compose
   * cleanly into a GET URL and round-trip without escaping concerns.
   * Limit defaults to 50, capped at 200 server-side.
   */
  ListMessages: {
    method: "GET" as const,
    path: "/api/conversations/threads/:threadId/messages",
    PathVars: PathThreadId,
    Query: z.object({
      limit: z.coerce.number().int().min(1).max(200).optional(),
      /** ISO datetime; omit on first call to walk from the newest message. */
      cursorCreatedAt: z.string().datetime().optional(),
      /** Message UUID at `cursorCreatedAt`; tie-breaks within the same instant. */
      cursorMessageId: z.string().uuid().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        messages: z.array(ThreadMessageSchema),
        nextCursor: ThreadMessageCursorSchema.nullable()
      })
    )
  }
} as const;

/**
 * Inferred TypeScript types
 */
import type { InferSchemas } from "../../schema-inference";
export type TApiConversations = InferSchemas<typeof ZConversations>;
